import { faGrid } from "@fortawesome/pro-light-svg-icons";
import { faChevronLeft } from "@fortawesome/pro-light-svg-icons/faChevronLeft";
import { faChevronRight } from "@fortawesome/pro-light-svg-icons/faChevronRight";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Box, Grid, IconButton, Stack, Typography } from '@mui/material';
import { MprcRequestErrorReport } from "components/MprcRequestErrorReport";
import { MprcRibbonListCardsWrapContainer } from "components/MprcRibbonList";
import { MprcRibbonScroll, MprcRibbonScrollHandle, MprcRibbonSlideReachedPositions } from "components/MprcRibbonScroll";
import { MprcSpinnerIndicator } from "components/MprcSpinnerIndicator";
import { useMprcTheme } from "components/MprcThemeProvider";
import React, { useEffect, useImperativeHandle, useState } from 'react';

export type MprcRibbonListHandle = {
    reset: () => void;
    replaceCard: (index: number, card: React.ReactNode, expanded: boolean) => void;
};

interface MprcListRibbonProps extends React.PropsWithChildren {
    id: any;
    index: number;
    title: string;
    slideWidth: number;
    gapWidth: number;
    maxItems?: number;
    expanded: boolean;
    visible: boolean;
    ribbonCardLoadMore?: React.ReactNode;
    onExpandClick?: () => void;
    onGetCards: (id: any, expanded: boolean, index: number) => Promise<React.ReactNode[]>;
    onEmptyDataSet?: () => void;
}

const MprcRibbonListComponent: React.ForwardRefRenderFunction<MprcRibbonListHandle, MprcListRibbonProps> = (props, forwardRef) => {
    const { id, index, visible, title, expanded, slideWidth, gapWidth, maxItems, ribbonCardLoadMore, onExpandClick, onGetCards, onEmptyDataSet } = props;

    const { theme } = useMprcTheme();

    const ribbonRef = React.useRef<MprcRibbonScrollHandle | null>(null);
    const [slidePosition, setSlidePosition] = useState<MprcRibbonSlideReachedPositions>({ scrollX: 0, start: true, end: true });
    const [loading, setLoading] = useState<boolean>(true);
    const [ribbonChildren, setRibbonChildren] = useState<React.ReactNode[] | null>(null);
    const [expandChildren, setExpandChildren] = useState<React.ReactNode[] | null>(null);
    const [viewError, setViewError] = useState<any>();

    /////////////////////////////////////////////////////////////////////////////////////////////////////////

    useImperativeHandle(forwardRef, () => ({
        reset: () => { setRibbonChildren(null); setExpandChildren(null); },
        replaceCard: (index: number, card: React.ReactNode, expanded: boolean) => {
            if (expanded && expandChildren != null) {
                setExpandChildren(expandChildren.map((c, i) => i != index ? c : card));
            }
            else if (!expanded && ribbonChildren != null) {
                setRibbonChildren(ribbonChildren.map((c, i) => i != index ? c : card));
            }
        }
    }));

    /////////////////////////////////////////////////////////////////////////////////////////////////////////

    useEffect(() => {
        async function load() {
            setLoading(true);
            try {
                const c = await onGetCards(id, expanded, index);
                if (expanded) setExpandChildren(c);
                else setRibbonChildren(c);

                if (c.length == 0 && onEmptyDataSet) onEmptyDataSet();
            }
            catch (e) {
                setViewError(e);
            }
            finally {
                setLoading(false);
            }
        }
        if ((expanded && expandChildren == null) || (!expanded && ribbonChildren == null)) load();
    }, [expanded, ribbonChildren, expandChildren]);

    return !viewError && !loading && React.Children.count(ribbonChildren) == 0
        ? null
        : (
            <Stack direction="column" spacing={1} sx={{ display: visible ? "" : "none", mb: 2, }}>
                <Grid container justifyContent="center" alignItems="center">
                    <Grid size="grow">
                        <Typography variant="h3" textTransform="unset" sx={theme => ({ ml: theme.spacing(1) })}>{title}</Typography>
                    </Grid>
                    {
                        !loading &&
                        <Grid>
                            {
                                onExpandClick && (slidePosition.start == false || slidePosition.end == false) &&
                                <IconButton onClick={onExpandClick} color={expanded ? "warning" : "inherit"}><FontAwesomeIcon title={expanded ? theme.MprcRibbonList.collapseTooltip : theme.MprcRibbonList.expandTooltip} icon={faGrid} /></IconButton>
                            }
                            {
                                !expanded && (slidePosition.start == false || slidePosition.end == false) &&
                                <>
                                    <IconButton title={theme.MprcRibbonList.slidePreviousTooltip} disabled={slidePosition.start} onClick={() => ribbonRef.current?.slide(0 - slideWidth)}><FontAwesomeIcon icon={faChevronLeft} /></IconButton>
                                    <IconButton title={theme.MprcRibbonList.slideNextTooltip} disabled={slidePosition.end} onClick={() => ribbonRef.current?.slide(slideWidth)}><FontAwesomeIcon icon={faChevronRight} /></IconButton>
                                </>
                            }
                        </Grid>
                    }
                </Grid>
                {
                    viewError
                        ? <MprcRequestErrorReport error={viewError} />
                        : loading
                            ? <section id="loading"><Box sx={theme => ({ ml: theme.spacing(1) })}><MprcSpinnerIndicator /></Box></section>
                            : expanded
                                ? (
                                    <MprcRibbonListCardsWrapContainer gapWidth={gapWidth}>
                                        {expandChildren}
                                    </MprcRibbonListCardsWrapContainer>
                                )
                                : (

                                    <MprcRibbonScroll showNavigationButtons={false} ref={ribbonRef} onPositionChanged={position => setSlidePosition(position)}>
                                        <MprcRibbonListCardsWrapContainer gapWidth={gapWidth} flexWrap="nowrap">
                                            {
                                                maxItems == undefined
                                                    ? ribbonChildren
                                                    : React.Children.map(ribbonChildren, (c, index) => index < maxItems ? c : null)
                                            }
                                            {
                                                maxItems != undefined && React.Children.count(ribbonChildren) > maxItems && ribbonCardLoadMore != null
                                                    ? ribbonCardLoadMore
                                                    : null
                                            }
                                        </MprcRibbonListCardsWrapContainer>
                                    </MprcRibbonScroll>
                                )
                }
            </Stack>
        );
};

export const MprcRibbonList = React.forwardRef(MprcRibbonListComponent);
